/**
 * Minimal Gregorian -> Bangla (Bangladesh civil) calendar converter.
 * Bangladesh uses the revised Bangla calendar where Boishakh 1 = April 14.
 */
const BANGLA_MONTHS_BN = [
  "বৈশাখ", "জ্যৈষ্ঠ", "আষাঢ়", "শ্রাবণ", "ভাদ্র", "আশ্বিন",
  "কার্তিক", "অগ্রহায়ণ", "পৌষ", "মাঘ", "ফাল্গুন", "চৈত্র",
];

const BANGLA_MONTHS_EN = [
  "Boishakh", "Joishtho", "Ashar", "Srabon", "Bhadro", "Ashwin",
  "Kartik", "Ogrohayon", "Poush", "Magh", "Falgun", "Choitro",
];

// Cumulative days at start of each Bangla month (revised civil calendar)
// Boishakh-Bhadro = 31 days (Apr14 ~ Sep15), Ashwin-Choitro = 30 days, Falgun = 30/31 (leap)
function banglaMonthLengths(banglaYear: number): number[] {
  // Leap rule mirrors Gregorian: corresponding Gregorian year is banglaYear + 593 (for months Jan-Apr13 of next greg year)
  const greg = banglaYear + 594;
  const isLeap = (greg % 4 === 0 && greg % 100 !== 0) || greg % 400 === 0;
  return [31, 31, 31, 31, 31, 30, 30, 30, 30, 30, isLeap ? 31 : 30, 30];
}

export interface BanglaDate {
  day: number;
  monthIndex: number;
  year: number;
  monthEn: string;
  monthBn: string;
}

export function gregorianToBangla(date: Date): BanglaDate {
  const year = date.getFullYear();
  const startOfBanglaYear = new Date(year, 3, 14); // April 14
  let banglaYear: number;
  let dayOfYear: number;

  if (date >= startOfBanglaYear) {
    banglaYear = year - 593;
    dayOfYear = Math.floor((date.getTime() - startOfBanglaYear.getTime()) / 86400000);
  } else {
    const prevStart = new Date(year - 1, 3, 14);
    banglaYear = year - 594;
    dayOfYear = Math.floor((date.getTime() - prevStart.getTime()) / 86400000);
  }

  const lengths = banglaMonthLengths(banglaYear);
  let monthIndex = 0;
  let day = dayOfYear + 1;
  while (monthIndex < 12 && day > lengths[monthIndex]) {
    day -= lengths[monthIndex];
    monthIndex++;
  }

  return {
    day,
    monthIndex,
    year: banglaYear,
    monthEn: BANGLA_MONTHS_EN[monthIndex],
    monthBn: BANGLA_MONTHS_BN[monthIndex],
  };
}

import { toBengaliNumerals } from "./bdNumerals";

export function formatBanglaDate(d: BanglaDate, lang: "en" | "bn" = "bn"): string {
  if (lang === "bn") {
    return `${toBengaliNumerals(d.day)} ${d.monthBn}, ${toBengaliNumerals(d.year)}`;
  }
  return `${d.day} ${d.monthEn}, ${d.year}`;
}
